<template>
  <SectionContainer bg-color="light" class="lead-form-page" height="none">
    <HeaderBenefits class="lead-form-page__benefits" />

    <template v-if="state.status === 'pending'">
      <div class="lead-form-page__main">
        <PCard class="lead-form-page__column--form">
          <SHeadingSkeleton
            class="lead-form-page__heading"
            size="2"
            width="300px"
          />
          <STextSkeleton :lines="8" />
        </PCard>
        <PCard>
          <aside class="lead-form-page__column--sidebar">
            <STextSkeleton :lines="4" />
          </aside>
        </PCard>
      </div>
    </template>

    <SErrorMessage
      v-else-if="state.status === 'error'"
      retry
      size="lg"
      @retry="refetch"
    />

    <template v-else-if="state.status === 'success'">
      <div class="lead-form-page__main">
        <PCard class="lead-form-page__column--form">
          <SHeading
            class="lead-form-page__heading"
            color="primary"
            level="1"
            size="2"
          >
            {{ pageTitle }}
          </SHeading>
          <ClientOnly>
            <lazyLeadForm
              v-if="state.data.formGroupId || state.data.formId"
              :customer-id="customerId ?? undefined"
              :feature-flags="{ prefillByUserData: true }"
              :form-group-id="state.data.formGroupId"
              :form-id="state.data.formId"
            />
          </ClientOnly>
        </PCard>
        <PCard>
          <aside class="lead-form-page__column--sidebar">
            <img
              v-if="sidebarImage"
              :alt="sidebarImage.alt || pageTitle"
              class="lead-form-page__sidebar-image"
              :src="sidebarImage.url"
            />
            <ul class="lead-form-page__trust-list">
              <li
                v-for="(tag, index) in trustTags"
                :key="index"
                class="lead-form-page__trust-tag"
              >
                <ContentIcon :icon="tag.icon" />
                <p v-html="tag.text" />
              </li>
            </ul>
          </aside>
        </PCard>
      </div>
    </template>
  </SectionContainer>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { useMounted } from "@vueuse/core";
import { useRoute } from "#app";
import { computed, defineAsyncComponent } from "vue";

import { useCompanyCardQuery } from "@solvari/common-fe/composables";
import {
  PCard,
  SErrorMessage,
  SHeading,
  SHeadingSkeleton,
  STextSkeleton,
} from "@solvari/components";
import { useI18nMainSiteProduct } from "@solvari/translations";

import { useLeadFormPage } from "~/features/product/api/leadForm.api.ts";
import ContentIcon from "~/shared/components/content-blocks/internal/ContentIcon.vue";
import HeaderBenefits from "~/shared/components/header/HeaderBenefits.vue";
import SectionContainer from "~/shared/components/SectionContainer.vue";
import { useHostLocale } from "~/shared/composables/useHost.ts";
import { useSeoNormalized } from "~/shared/composables/useMetaData.ts";
import { useRequiredParams } from "~/shared/libs/routeParams.ts";

const lazyLeadForm = defineAsyncComponent(
  () => import("~/shared/components/content-blocks/leadForm.ts"),
);

const locale = useHostLocale();
const { geoProductSlug } = useRequiredParams("geoProductSlug");

const route = useRoute();

const customerId = computed(() => {
  const param = route.query.customerId;
  return typeof param === "string" ? param : null;
});

const formSlug = computed(() => {
  const param = route.params.formSlug;
  return typeof param === "string" ? param : null;
});

const { state, refetch } = useQuery(
  useLeadFormPage({
    formSlug,
    geoProductCategoryUri: `/${geoProductSlug}`,
    locale,
  }),
);

// Server renders the default title; the customer's company only applies client-side
const isMounted = useMounted();

const { data: companyData } = useQuery({
  ...useCompanyCardQuery(customerId, locale),
  enabled: () => isMounted.value && !!customerId.value,
});

const { tr } = useI18nMainSiteProduct();

const pageTitle = computed(() => {
  if (state.value.status !== "success") {
    return "";
  }
  const { title, productName } = state.value.data;

  if (companyData.value?.name) {
    return tr("main_site.product.lead_form.header_title_company", {
      productName,
      customerName: companyData.value.name,
    });
  }

  return (
    title || tr("main_site.product.lead_form.header_title", { productName })
  );
});

const sidebarImage = computed(() => {
  if (state.value.status !== "success") {
    return null;
  }
  return state.value.data.image;
});

useSeoNormalized({
  title: () => pageTitle.value || undefined,
  index: false,
});

const trustTags = computed(() => [
  {
    icon: "far fa-clock",
    text: tr("main_site.product.lead_form.sidebar_last_request_minutes_ago", {
      minutes: "2",
    }),
  },
  {
    icon: "far fa-chart-bar",
    text: tr("main_site.product.lead_form.sidebar_requests_average", {
      amount: "919",
    }),
  },
  {
    icon: "far fa-users",
    text: tr("main_site.product.lead_form.sidebar_people_before_you", {
      amount: "2.013.967",
    }),
  },
  {
    icon: "far fa-award",
    text: tr("main_site.product.lead_form.sidebar_years_of_experience"),
  },
]);
</script>

<style lang="postcss">
.lead-form-page {
  @apply flex flex-grow flex-col pb-20;

  &__benefits {
    @apply py-4 pb-7;
  }

  &__main {
    @apply flex flex-col items-center gap-8 lg:flex-row lg:items-start;
  }

  &__heading.s-heading {
    @apply mb-7 mt-2;
  }

  &__column--form {
    @apply grow;
  }

  &__column--sidebar {
    @apply w-80;
  }

  &__sidebar-image {
    @apply mb-6 w-full rounded-xl object-cover;
  }

  &__trust-list {
    @apply flex flex-col gap-4;
  }

  &__trust-tag {
    @apply flex items-center gap-4;
  }
}
</style>
